// Parsing for the standard custom-script JSON format (as produced by the community Script Tool): a
// flat array, optionally with a {"id":"_meta","name":...} entry, where every other entry is a
// character id or an object with an "id".
//
// Official characters only (requirements §3): an unrecognised id stays listed but gets no ability
// text, icon or night action.
import { getBaseCharacter } from "../characters/data.js";
const VALID_TEAMS = ["townsfolk", "outsider", "minion", "demon", "traveler", "fabled"];
export function normalizeCharacterId(value) {
    return String(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}
function normalizeTeam(team) {
    if (!team)
        return null;
    const t = String(team).toLowerCase();
    if (t === "traveller" || t === "traveler")
        return "traveler"; // accept both spellings
    return VALID_TEAMS.includes(t) ? t : null;
}
// A script may carry its own night-action metadata. Malformed parts are dropped rather than
// rejected, falling back to the built-in data.
function normalizeNightAction(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const r = raw;
    const picks = Array.isArray(r["picks"])
        ? r["picks"]
            .map((p) => {
            if (!p || typeof p !== "object")
                return null;
            const o = p;
            const pick = { type: o["type"] === "character" ? "character" : "player" };
            if (o["excludeSelf"])
                pick.excludeSelf = true;
            if (o["optional"])
                pick.optional = true;
            if (typeof o["label"] === "string")
                pick.label = o["label"];
            return pick;
        })
            .filter((p) => p !== null)
        : [{ type: "player" }]; // sensible default: "choose a player"
    if (picks.length === 0)
        return null;
    const action = { picks };
    if (typeof r["firstNight"] === "number" && Number.isFinite(r["firstNight"]))
        action.firstNight = r["firstNight"];
    if (typeof r["otherNight"] === "number" && Number.isFinite(r["otherNight"]))
        action.otherNight = r["otherNight"];
    if (action.firstNight === undefined && action.otherNight === undefined)
        return null;
    action.prompt = typeof r["prompt"] === "string" && r["prompt"] ? r["prompt"] : "Choose a target.";
    if (typeof r["note"] === "string" && r["note"])
        action.note = r["note"];
    if (r["oncePerGame"])
        action.oncePerGame = true;
    return action;
}
function titleCase(id) {
    return id.charAt(0).toUpperCase() + id.slice(1);
}
export class ScriptParseError extends Error {
}
export function parseScriptJson(json) {
    if (!Array.isArray(json)) {
        throw new ScriptParseError("Expected a JSON array (a Blood on the Clocktower script file).");
    }
    let name = "Custom Script";
    const characters = [];
    const seen = new Set();
    for (const entry of json) {
        if (entry && typeof entry === "object" && entry["id"] === "_meta") {
            const meta = entry;
            if (typeof meta["name"] === "string" && meta["name"])
                name = meta["name"];
            continue;
        }
        let id = "";
        let charName;
        let team = null;
        let ability = null;
        let nightAction = null;
        if (typeof entry === "string") {
            id = normalizeCharacterId(entry);
        }
        else if (entry && typeof entry === "object" && entry["id"]) {
            const o = entry;
            id = normalizeCharacterId(o["id"]);
            if (typeof o["name"] === "string")
                charName = o["name"];
            team = normalizeTeam(o["team"]);
            ability = typeof o["ability"] === "string" ? o["ability"] : null;
            nightAction = normalizeNightAction(o["nightAction"]);
        }
        if (!id || seen.has(id))
            continue;
        seen.add(id);
        const known = getBaseCharacter(id);
        characters.push({
            id,
            name: charName || known?.name || titleCase(id),
            team: team ?? known?.team ?? null,
            ability: ability ?? known?.ability ?? null,
            nightAction: nightAction ?? known?.nightAction ?? null,
            icon: known?.icon ?? null,
            // Fall back to the base character's wake position, or the Night Order panel empties for
            // imported scripts.
            firstNightWake: known?.firstNightWake ?? null,
            otherNightWake: known?.otherNightWake ?? null,
        });
    }
    if (characters.length === 0)
        throw new ScriptParseError("No characters found in that file.");
    return { name, characters };
}
export function parseScriptText(text) {
    let json;
    try {
        json = JSON.parse(text);
    }
    catch {
        throw new ScriptParseError("That isn't valid JSON.");
    }
    return parseScriptJson(json);
}
/**
 * The raw object entries of a script file by normalised id, so the editor can hand back a
 * character's custom ability or night-action data untouched.
 */
export function rawScriptEntries(json) {
    const out = new Map();
    if (!Array.isArray(json))
        return out;
    for (const entry of json) {
        if (!entry || typeof entry !== "object")
            continue;
        const o = entry;
        if (o["id"] === "_meta" || !o["id"])
            continue;
        out.set(normalizeCharacterId(o["id"]), o);
    }
    return out;
}
/** Writes a script back out as standard script JSON: a `_meta` entry, then the characters in order. */
export function buildScriptJson(name, ids, raw) {
    return [{ id: "_meta", name }, ...ids.map((id) => raw?.get(id) ?? id)];
}
//# sourceMappingURL=script.js.map